import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  setUser as setU,
  setVerify as setV,
  setAuth as setA,
  setToken as setT,
  setWaitingVerification as setWV,
  setAttempts as setAttep,
  restore as reset,
} from './auth.slice';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import {
  useSignUpMutation,
  useSignUpGuestMutation,
  useSweepstakesRegisterMutation,
  useVerifyCodeMutation,
  useResendCodeMutation,
} from './auth.api';
import { useBooking } from '@/store';
import { useSignInMutation } from './auth-v3.api';
import { useModals } from '@mattjennings/react-modal-stack';
import { AlertDialog } from '../../components/dialogs';
import useStepper from '@/hooks/checkout/useStepper';
import useCallApiProcess from '@/hooks/useCallApiProcess';
import useAnalyticsEvents from '@/hooks/useAnalyticsEvents';
import useExchange from '../exchange/useExchange';
import useErrorTracking from '@/hooks/userTracking/useErrorTracking';

const skipOTP = process.env.NEXT_PUBLIC_SKIP_OTP
  ? process.env.NEXT_PUBLIC_SKIP_OTP === 'true'
  : false;

export default function useAuth() {
  const { t } = useTranslation();
  const {
    signUp: signUpEvent,
    signUpGuest: signUpGuestEvent,
    signIn: signInEvent,
    verifyUser: verifyUserEvent,
    verifyUserError,
    resendCode: resendCodeEvent,
    posthogIdentify,
  } = useAnalyticsEvents();
  const { openModal, closeModal } = useModals();
  const { setBookingOrder, setBookingId } = useBooking();
  const dispatch = useDispatch();
  const [singUpUser] = useSignUpMutation();
  const { callApi: callApiSingUpUser } = useCallApiProcess(
    singUpUser,
    t('checkout.loadings.pleaseWait'),
  );
  const [singInUser] = useSignInMutation();
  const { callApi: callApiSingInUser } = useCallApiProcess(
    singInUser,
    t('checkout.loadings.pleaseWait'),
  );
  const [singUpGuest] = useSignUpGuestMutation();
  const { callApi: callApiSingUpGuest } = useCallApiProcess(
    singUpGuest,
    t('checkout.loadings.pleaseWait'),
  );
  const [sweepstakesRegistration] = useSweepstakesRegisterMutation();
  const { callApi: callApiSweepstakesRegistration } = useCallApiProcess(
    sweepstakesRegistration,
    t('checkout.loadings.pleaseWait'),
  );
  const [verifyCode] = useVerifyCodeMutation();
  const [resendVerifyCode] = useResendCodeMutation();
  const [currentPath, setCurrentPath] = useState('');
  const router = useRouter();

  const { setExchangeToken } = useExchange();

  const { nextStep, prevStep } = useStepper();

  const { checkoutErrorShown } = useErrorTracking();

  const booking = useSelector((state) => state.booking);
  const auth = useSelector((state) => state.auth);
  const event = useSelector((state) => state.event);

  const isVerified = auth.verified;
  const isWaitingVerification = auth.waitingVerification;
  const isGuest = auth.guest;
  const isAuth = auth.user && auth.verified;
  const role = auth.token ? auth.token.role : '';
  const isSeller =
    auth.token && (auth.token.role === 'owner' || auth.token.role === 'seller');

  const sendError = (error) => {
    let message = 'Error';
    if (typeof error === 'string') {
      message = error;
    } else if (typeof error.error === 'string') {
      message = error.error;
    }
    openModal(AlertDialog, {
      title: 'Error',
      text: message,
      acceptLabel: 'Aceptar',
    });
    return { error: message };
  };

  const setAuth = (d) => {
    dispatch(setA(d));
  };

  const setVerify = (v) => {
    dispatch(setV(v));
  };

  const setUser = (u) => {
    dispatch(setU(u));
  };

  const setToken = (t) => {
    dispatch(setT(t));
  };

  const setWaitingVerification = (v) => {
    dispatch(setWV(v));
  };

  const withExchangeToken = (apiFunc) => {
    return async (...args) => {
      try {
        const response = await apiFunc(...args);
        if (response) {
          const newExchangeToken =
            response?.exchange_token ||
            response?.data?.exchange_token ||
            response?.error?.data?.exchange_token;

          if (newExchangeToken) setExchangeToken(newExchangeToken);
        }
        return response;
      } catch (error) {
        throw error;
      }
    };
  };

  const signUp = withExchangeToken(async ({ name, phone, email, password }) => {
    const callApiWithExchangeToken = withExchangeToken(callApiSingUpUser);
    try {
      const response = await callApiWithExchangeToken({
        name,
        email,
        phone,
        password,
      });

      signUpEvent({ name, email, phone });
      if (!response.error) {
        setAuth({
          user: {
            name,
            email,
            phone,
          },
          guest: false,
          verified: false,
          waitingVerification: true,
        });
        router.push('/auth');
      } else {
        router.push('/500');
      }
    } catch { }
  });

  const signUpGuest = async ({
    name,
    email,
    phone,
    country,
    media = 'sms',
    bookingOrder,
    bookingId,
    preRegisterId,
    dispatch_id,
  }) => {
    try {
      const callApiWithExchangeToken = withExchangeToken(callApiSingUpGuest);
      const response = await callApiWithExchangeToken({
        email,
        name,
        phone,
        event_id: event.event.id,
        media,
        bookingOrder,
        bookingId,
        preRegisterId,
        dispatch_id,
      });

      signUpGuestEvent({
        email,
        phone,
        event_id: event.event.id,
      });
      if (!response.error) {
        setAuth({
          user: {
            email,
            phone,
            name,
            country: country ? JSON.parse(country) : null,
            method: media,
          },
          guest: true,
          verified: false,
          waitingVerification: true,
        });
        setToken({
          user_info: {
            user_id: response?.data?.data.id,
          },
        });
        return response;
      }
      return null;
    } catch (e) {
    }

    return null;
  };

  const sweepstakesSignUpGuest = async ({
    name,
    email,
    phone,
    country,
    media = 'sms',
    bookingOrder,
    bookingId,
    preRegisterId,
    dispatch_id,
  }) => {
    try {
      const callApiWithExchangeToken = withExchangeToken(callApiSingUpGuest);
      const response = await callApiWithExchangeToken({
        email,
        name,
        phone,
        event_id: event.event.id,
        media,
        bookingOrder,
        bookingId,
        preRegisterId,
        dispatch_id,
      });

      signUpGuestEvent({
        email,
        phone,
        event_id: event.event.id,
      });
      if (!response.error) {
        setAuth({
          user: {
            email,
            phone,
            name,
            country: country ? JSON.parse(country) : null,
            method: media,
          },
          guest: true,
          verified: false,
          waitingVerification: true,
        });
        setToken({
          user_info: {
            user_id: response?.data?.data.id,
          },
        });
        return response;
      }
      if (response.error) return response.error
      return null;
    } catch (e) {
    }

    return null;
  };


  const sweepstakesRegister = async (data) => {
    try {
      const callApiWithExchangeToken = withExchangeToken(
        callApiSweepstakesRegistration,
      );
      const response = await callApiWithExchangeToken(data);

      if (!response.error) {
        const { data } = response;

        setBookingOrder(data.data.booking_number);
        setBookingId(data.data.booking_id);

        if (data.data.booking_id && data.data.booking_number) {
          const res = await signUpGuest({
            name: data.data.name,
            email: data.data.email,
            phone: data.data.phone_number,
            media: 'whatsapp',
            bookingOrder: data.data.booking_number,
            bookingId: data.data.booking_id,
          });

          return res;
        }
      }
      if (response.error) return response.error
      return null
    } catch { }
    return null;
  };
  const verifyAppUser = async ({
    token,
    country,
    media = 'sms',
    bookingOrder,
    bookingId,
  }) => {
    try {
      const callApiWithExchangeToken = withExchangeToken(callApiSingUpGuest);
      const response = await callApiWithExchangeToken({
        token,
        event_id: event.event.id,
        media,
        bookingOrder,
        bookingId,
      });

      if (response.error) {
        throw 'authError';
      }

      if (!response?.data?.data?.name || !response?.data?.data?.phone) {
        throw 'authError';
      }
      setAuth({
        user: {
          email: response?.data.data.email,
          phone: response?.data.data.phone,
          name: response?.data.data.name,
          country: country ? JSON.parse(country) : null,
          method: media,
        },
        guest: true,
        verified: false,
        waitingVerification: false,
      });
      setToken({
        user_info: {
          user_id: response?.data?.data.id,
        },
      });
      setVerify(true);

      const phDistinctId = router.query?.ph_distinct_id;
      if (phDistinctId) {
        posthogIdentify(phDistinctId);
      }

      nextStep();
      return response;
    } catch (error) {
      return null;
    }
    return null;
  };

  const verifyUser = async ({ code, bookingOrder, bookingId }) => {
    if (skipOTP) {
      if (code === '000000') {
        setVerify(true);

        verifyUserEvent({
          email: auth.user.email,
          code,
        });
        return {};
      }
    }
    try {
      const verifyCodeWithExchangeToken = withExchangeToken(verifyCode);
      const response = await verifyCodeWithExchangeToken({
        email: auth.user.email,
        code,
        bookingOrder,
        bookingId,
      });

      if (!response.error) {
        setVerify(true);
        verifyUserEvent({ email: auth.user.email, code });
      } else {
        verifyUserError({ email: auth.user.email, code });
      }
      return response;
    } catch (e) {
      console.log('verifyUser error', e);
    }
    return null;
  };

  const signIn = async ({ email, password }) => {
    try {
      const callApiWithExchangeToken = withExchangeToken(callApiSingInUser);
      setUser({ email });
      const response = await callApiWithExchangeToken({
        email,
        password,
        booking_id: booking.orderInfo?.id,
        event_id: event.event.id,
      });

      if (!response.error) {
        if (
          response?.data?.data?.user_info &&
          (response?.data?.data.role === 'owner' ||
            response?.data?.data.role === 'seller')
        ) {
          if (response?.data?.data.user_info.phone_number) {
            setVerify(true);
            setToken(response?.data?.data);
            signInEvent({ email, ...response?.data?.data });
            return response;
          } else {
            checkoutErrorShown({
              source: 'backend',
              code: undefined,
              message: t('checkout.auth.loginErrorPhone'),
            });
            openModal(AlertDialog, {
              title: t('checkout.auth.loginErrorTitle'),
              text: t('checkout.auth.loginErrorPhone'),
              acceptLabel: t('checkout.global.acceptLabel'),
            });
          }
        } else {
          checkoutErrorShown({
            source: 'backend',
            code: undefined,
            message: t('checkout.auth.loginErrorAuth'),
          });
          openModal(AlertDialog, {
            title: t('checkout.auth.loginErrorTitle'),
            text: t('checkout.auth.loginErrorAuth'),
            acceptLabel: t('checkout.global.acceptLabel'),
          });
        }
      }
    } catch (e) { }
    return null;
  };

  const resendCode = async (media = 'sms', dispatch_id) => {
    try {
      const resendVerifyCodeWithExchangeToken =
        withExchangeToken(resendVerifyCode);
      const response = await resendVerifyCodeWithExchangeToken({
        email: auth.user.email,
        media,
        dispatch_id,
      });

      resendCodeEvent({ email: auth.user.email });
      setAttep(auth.attemps + 1);
    } catch { }
  };

  const restore = () => {
    dispatch(reset());
  };

  return {
    auth,
    role,
    isAuth,
    isVerified,
    isWaitingVerification,
    isGuest,
    isSeller,
    setCurrentPath,
    signUpGuest,
    sweepstakesSignUpGuest,
    sweepstakesRegister,
    signUp,
    verifyUser,
    verifyAppUser,
    signIn,
    resendCode,
    setWaitingVerification,
    restore,
  };
}
